import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Leaf } from "lucide-react";

/**
 * KOVA — Public Privacy Policy page.
 *
 * PUBLIC route. No auth wrapper, no protected data, no API calls.
 * Reachable without a session at `/privacy-policy` and on production at
 * https://kova.tzat.co.in/privacy-policy — required to publish the Google
 * OAuth consent screen for KOVA's Drive-backup integration.
 *
 * Content is written to accurately describe what the existing KOVA
 * application already does (backend/backups.py + auth + business profile).
 * No claim is made about data KOVA does not actually collect.
 */
export default function PrivacyPolicy() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Privacy Policy · KOVA";
    return () => { document.title = prev; };
  }, []);

  const lastUpdated = "February 23, 2026";

  return (
    <div className="min-h-screen bg-[#0B0F0C] text-[#F8F9FA]" data-testid="privacy-policy-page">
      {/* Header strip — matches KOVA dark aesthetic without pulling in the auth Layout */}
      <header className="border-b border-[#3A4640] bg-[#121815]">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/login"
            className="flex items-center gap-2.5 group"
            data-testid="privacy-policy-home-link"
          >
            <span className="w-8 h-8 rounded-md bg-[#0B0F0C] border border-[#39FF14]/30 flex items-center justify-center">
              <Leaf size={16} className="text-[#39FF14]" />
            </span>
            <span className="font-heading font-bold tracking-tight text-[#F8F9FA]">
              KOVA <span className="text-[#8A8F98] font-normal text-xs ml-1">· Three Zee Agro Tech</span>
            </span>
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs text-[#8A8F98] hover:text-[#39FF14] transition-colors font-heading"
            data-testid="privacy-policy-back-link"
          >
            <ArrowLeft size={12} /> Back to sign in
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-3">
          <ShieldCheck size={22} className="text-[#39FF14]" />
          <p className="text-xs uppercase tracking-widest text-[#39FF14] font-heading">Legal</p>
        </div>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight text-[#F8F9FA] mb-3"
          data-testid="privacy-policy-title"
        >
          KOVA Privacy Policy
        </h1>
        <p className="text-sm text-[#8A8F98] mb-10 font-mono">
          Last updated: {lastUpdated}
        </p>

        <div className="space-y-10 text-[15px] leading-relaxed text-[#F8F9FA]/90">

          <section>
            <p>
              KOVA is the internal operations platform built for and used by{" "}
              <strong className="text-[#F8F9FA]">Three Zee Agro Tech (TZAT)</strong>, an
              agricultural machinery dealership. This Privacy Policy describes what
              information KOVA collects, how that information is used, and the
              third-party integrations KOVA relies on to operate. KOVA is not offered
              as a public service; access to KOVA requires an account provisioned by
              a Three Zee Agro Tech administrator.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-heading font-bold text-[#39FF14] mb-3">
              1. What information KOVA may collect
            </h2>
            <p className="mb-3">
              KOVA stores only the operational information that TZAT staff enter or
              generate while running the business. This includes:
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-[#F8F9FA]/85">
              <li><strong>Staff accounts</strong> — name, email address, role, and a securely hashed password for KOVA login.</li>
              <li><strong>Customer records</strong> — name, contact number(s), address, village, block, district, state, and other agricultural-dealership fields entered by TZAT staff.</li>
              <li><strong>Sales, service, subsidy, machine, follow-up and product records</strong> — the business documents TZAT staff create for their own dealership operations.</li>
              <li><strong>Interaction and timeline records</strong> — notes, call outcomes and status changes recorded by TZAT staff during customer engagement.</li>
              <li><strong>Backup metadata</strong> — timestamps and file identifiers for the database backups KOVA creates.</li>
            </ul>
            <p className="mt-3 text-sm text-[#8A8F98]">
              KOVA does not collect information from visitors who are not signed in.
              KOVA does not use tracking cookies for advertising and does not sell
              information to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-heading font-bold text-[#39FF14] mb-3">
              2. How information is used
            </h2>
            <p>
              The information stored in KOVA is used solely to run the day-to-day
              operations of Three Zee Agro Tech: managing customers and inventory,
              generating invoices, tracking service jobs, running subsidy workflows,
              scheduling follow-ups, and sending operational communication (for
              example, service reminders) to customers who have provided contact
              details for that purpose. Aggregate dashboards (revenue, top customers,
              pending payments) are computed from this same data and shown only to
              signed-in staff with the appropriate role.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-heading font-bold text-[#39FF14] mb-3">
              3. Data storage and security
            </h2>
            <p>
              KOVA data is stored in a managed MongoDB database dedicated to Three
              Zee Agro Tech. Access is protected by authenticated sessions with
              role-based permissions; passwords are stored as one-way hashes. Deleted
              records are soft-deleted and remain recoverable only by administrators
              via the Trash view. Every write operation is scoped to the signed-in
              user identity and is subject to KOVA's permission matrix.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-heading font-bold text-[#39FF14] mb-3">
              4. Third-party services and integrations
            </h2>
            <p className="mb-3">KOVA integrates with a small number of external services in order to operate:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-[#F8F9FA]/85">
              <li>
                <strong>Google Drive</strong> — for storing encrypted database
                backups. See section 5 below for the exact scope KOVA requests.
              </li>
              <li>
                <strong>Twilio</strong> — for sending SMS and WhatsApp follow-up
                reminders to customers who have provided a mobile number, only when
                a TZAT staff member explicitly triggers the message.
              </li>
              <li>
                <strong>Product analytics (PostHog)</strong> — used only to help TZAT
                understand which internal screens their own staff use. No customer
                records are transmitted to this service.
              </li>
            </ul>
            <p className="mt-3">
              KOVA does not share the information it stores with any third party
              other than the operational services listed above, and only to the
              minimum extent required for those services to function.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-heading font-bold text-[#39FF14] mb-3">
              5. Google Drive access for KOVA backups
            </h2>
            <p className="mb-3">
              KOVA uses Google OAuth 2.0 to obtain permission from the Three Zee
              Agro Tech administrator to upload database backup files to that
              administrator's own Google Drive. This is an opt-in configuration —
              KOVA will not connect to Google Drive unless the administrator
              explicitly runs the one-time OAuth bootstrap and provides the
              resulting credentials to KOVA.
            </p>
            <p className="mb-3">
              The Google Drive scope KOVA requests is:
            </p>
            <div className="bg-[#121815] border border-[#3A4640] rounded-md p-4 mb-3 font-mono text-xs text-[#39FF14] break-all">
              https://www.googleapis.com/auth/drive.file
            </div>
            <p className="mb-3">
              This is Google's per-file, non-sensitive scope. Under this scope KOVA
              can only see and manage the files and folders it has itself created —
              specifically the <code className="font-mono text-[#39FF14]">KOVA/Backups/</code>{" "}
              folder tree that KOVA generates for its own backup archives. KOVA
              cannot see, list, download or modify any other file already present in
              the administrator's Google Drive, and cannot access other Google
              Workspace data (Gmail, Calendar, Contacts, Photos, etc.).
            </p>
            <p>
              The credentials KOVA receives from Google (a client identifier and a
              refresh token belonging to the administrator) are stored only in
              KOVA's server-side environment configuration and are never displayed,
              logged, or transmitted anywhere other than to Google's own OAuth
              endpoints for the purpose of refreshing short-lived access tokens.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-heading font-bold text-[#39FF14] mb-3">
              6. User control and revocation of Google access
            </h2>
            <p className="mb-3">
              The Three Zee Agro Tech administrator can revoke KOVA's access to
              their Google Drive at any time, and control is entirely in their
              hands:
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-[#F8F9FA]/85">
              <li>
                Visit{" "}
                <a
                  href="https://myaccount.google.com/permissions"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-[#39FF14] hover:underline"
                >
                  https://myaccount.google.com/permissions
                </a>{" "}
                and remove "KOVA" from the list of connected apps. Backups will stop
                uploading to Drive immediately; already-uploaded files remain in the
                administrator's Drive and are unaffected.
              </li>
              <li>
                Alternatively, an administrator can remove the KOVA OAuth secrets
                from KOVA's server configuration. On the next KOVA restart, Drive
                uploads will simply be skipped.
              </li>
              <li>
                In either case, KOVA continues to create local backup files even
                when Google Drive access is not configured.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-heading font-bold text-[#39FF14] mb-3">
              7. Data retention
            </h2>
            <p>
              KOVA retains operational records for as long as they are useful to
              running the Three Zee Agro Tech dealership. Individual records can be
              soft-deleted by an authorised staff member and are then permanently
              removable from the Trash view by an administrator. Backup archives
              stored in Google Drive follow the retention policy configured by the
              TZAT administrator; older backups can be deleted directly from that
              administrator's Google Drive at any time.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-heading font-bold text-[#39FF14] mb-3">
              8. Contact information
            </h2>
            <p className="mb-3">
              For any question about this Privacy Policy, about how KOVA handles
              information, or to request removal of specific records:
            </p>
            <div className="bg-[#121815] border border-[#3A4640] rounded-md p-5 space-y-1.5">
              <div><span className="text-[#8A8F98] text-xs uppercase tracking-wider font-heading">Business</span><div className="text-[#F8F9FA] mt-0.5">Three Zee Agro Tech</div></div>
              <div><span className="text-[#8A8F98] text-xs uppercase tracking-wider font-heading">Email</span><div className="text-[#39FF14] mt-0.5 font-mono">contact@threezeeagrotech.com</div></div>
              <div><span className="text-[#8A8F98] text-xs uppercase tracking-wider font-heading">Website</span><div className="text-[#39FF14] mt-0.5 font-mono"><a href="https://kova.tzat.co.in" target="_blank" rel="noreferrer noopener" className="hover:underline">https://kova.tzat.co.in</a></div></div>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-heading font-bold text-[#39FF14] mb-3">
              9. Changes to this Privacy Policy
            </h2>
            <p>
              This Privacy Policy may be updated when KOVA introduces new
              integrations or changes how information is used. The "Last updated"
              date at the top of this page will always reflect the most recent
              revision.
            </p>
          </section>
        </div>

        <footer className="mt-14 pt-6 border-t border-[#3A4640] text-center text-[11px] text-[#8A8F98] font-mono">
          KOVA · Three Zee Agro Tech · Privacy Policy · v1
        </footer>
      </main>
    </div>
  );
}
